import { useState, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Button, Spinner, Carousel, Modal } from "react-bootstrap";
import { getStopsViewApi, getActualWorkflowApi, getEstimateWorkflowApi, deleteStopsApi } from "./stops-api";
import locationIcon from "../../assets/svg/location_ic.svg";
import moreIcon from "../../assets/svg/more_ic.svg";
import DeleteModal from "../common/deleteModel";
import dateIc from "../../assets/svg/date_ic.svg";
import TruckProgressBar from "./truck-progress-bar";
import dollerIc from "../../assets/svg/doller_ic.svg";
import workflowTick from "../../assets/svg/workflow-tick.svg";
import activeStop from "../../assets/svg/active-location.svg";
import activeWorkflow from "../../assets/svg/active-workflow.svg";
import workflowIcon from "../../assets/svg/workflow.svg";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import fromLocImg from "../../assets/png/from_location.png";
import toLocImg from "../../assets/png/to_location.png";
import RoadRoute from "../common/RoadRoute";
import { useGlobal } from "../../GlobalContext";
import ProfileImageModal from "../common/ProfileImageModal";
import CommonSnackbar from "../common/reusable-commponent";
import { sanitizeInput, validateUrl } from "../common/security-utils";
import WorkflowTab from "../trips/WorkflowTab";
import "./stops-view.css";
import "../common/skeleton-effect.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export const StopView = () => {
  const { rolePermissionList, userType } = useGlobal();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};
  const [viewStopDetails, setViewStopDetails] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("stops");
  const [actualWorkflow, setActualWorkflow] = useState([]);
  const [estimateWorkflow, setEstimateWorkflow] = useState([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState([]);
  const [selectedStop, setSelectedStop] = useState(-1);
  const [showProfilePopup, setShowProfilePopup] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);

  const handleSnackbarClose = () => setShowSnackbar(false);

  const checkPermission = (permission) => {
    if (!rolePermissionList || !Array.isArray(rolePermissionList)) return false;
    return rolePermissionList.findIndex((element) => element?.["name"] === "Stops" && element?.["menu_permission"] === true && element[permission] === true) > -1;
  };

  const getStopView = async (id) => {
    setIsLoading(true);
    try {
      const response = await getStopsViewApi(id);
      if (response?.data?.success === true) {
        setViewStopDetails(response.data.stops ?? {});
      }
    } catch (err) {
      setSnackbarMessage("Failed to fetch stop details");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getActualWorkflow = async (id) => {
    setIsWorkflowLoading(true);
    try {
      const response = await getActualWorkflowApi(id);
      if (response?.data?.success === true) {
        setActualWorkflow(
          (response.data.tripHistory ?? []).map((row) => ({
            ...row,
            attachments_count: row.attachmentscount ?? 0,
            attachments: row.attachments ?? [],
          }))
        );
      }
    } catch (err) {
      // Handled by apiCall
    } finally {
      setIsWorkflowLoading(false);
    }
  };

  const getEstimateWorkflow = async (id) => {
    setIsWorkflowLoading(true);
    try {
      const response = await getEstimateWorkflowApi(id);
      if (response?.data?.success === true) {
        setEstimateWorkflow(response.data.estimatedworkflow ?? []);
      }
    } catch (err) {
      // Handled by apiCall
    } finally {
      setIsWorkflowLoading(false);
    }
  };

  useEffect(() => {
    if (id != null) {
      getStopView(id);
      getActualWorkflow(id);
      getEstimateWorkflow(id);
    }
  }, [id]);

  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);
  const deleteVendor = async () => {
    try {
      const response = await deleteStopsApi(deleteId);
      if (response?.data?.success === true) {
        setSnackbarMessage(response.data.message || "Stop deleted successfully");
        setSnackbarStatus(true);
        setShowSnackbar(true);
        setTimeout(() => navigate(-1), 1500);
      } else {
        setSnackbarMessage(response?.data?.message || "Failed to delete stop");
        setSnackbarStatus(false);
        setShowSnackbar(true);
      }
    } catch (err) {
      setSnackbarMessage("An error occurred during deletion");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
      setIsOpenDelete(false);
    }
  };

  const createCustomFromMarker = () =>
    L.divIcon({
      className: "custom-icon",
      html: `
          <div style="position: relative; display: inline-block;">
            <!-- Your image from assets -->
            <img src="${fromLocImg}" style="width:40px;height:40px;" />
            
            <!-- Text overlay -->
            <span style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 14px;
              font-weight: bold;
            ">
             
            </span>
          </div>
        `,
      iconSize: [40, 40], // size of whole div
      iconAnchor: [20, 40], // bottom center
      popupAnchor: [0, -40],
    });
  const createCustomToMarker = () =>
    L.divIcon({
      className: "custom-icon",
      html: `
          <div style="position: relative; display: inline-block;">
            <!-- Your image from assets -->
            <img src="${toLocImg}" style="width:40px;height:40px;" />
            
            <!-- Text overlay -->
            <span style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 14px;
              font-weight: bold;
            ">
             
            </span>
          </div>
        `,
      iconSize: [40, 40], // size of whole div
      iconAnchor: [20, 40], // bottom center
      popupAnchor: [0, -40],
    });
  return (
    <>
      <DeleteModal
        title={"stops"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false); // close modal
          setDeleteId(null); // reset selected ID
        }}
        onDelete={deleteVendor}
      />
      <div className="d-flex ">
        <div className="rounded-4 flex-grow-1  " style={{ width: "calc(100vw - 80px)" }}>
          <div className="p-2 scroll-hidden" style={{ background: "#F5F7FA", height: "calc(100vh - 56px)" }}>
            <div className="py-2 stops-view-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center justify-content-start">
                <div className="ms-2 d-flex align-items-center justify-content-center rounded-5 back-btn-wrapper">
                  <MoveLeft
                    className="cursor-pointer"
                    onClick={() => {
                      navigate(-1);
                    }}
                  />
                </div>

                {isLoading ? (
                  <>
                    <div className="skeleton ms-3" style={{ width: "64px", height: "64px", borderRadius: "16px" }} />
                    <div className="ms-2 d-flex flex-column align-items-start justify-content-center gap-2">
                      <div className="skeleton skeleton-text" style={{ width: "180px", height: "20px" }} />
                      <div className="d-flex align-items-center gap-2">
                        <div className="skeleton skeleton-text" style={{ width: "120px", height: "13px" }} />
                        <div className="skeleton skeleton-text" style={{ width: "80px", height: "24px", borderRadius: "8px" }} />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="ms-3 d-flex align-items-center justify-content-center profile-img-container"
                      onClick={() => {
                        const profileUrl = viewStopDetails.profile;
                        if (profileUrl && validateUrl(profileUrl)) {
                          setShowProfilePopup(true);
                        }
                      }}
                    >
                      {viewStopDetails.profile ? (
                        <img
                          src={viewStopDetails.profile}
                          alt="profile"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                          style={{ width: "100%", height: "100%", backgroundColor: "#E8F0FE", color: "#3469E7" }}
                        >
                          {(viewStopDetails.customer_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="ms-2 d-flex flex-column align-items-start justify-content-center gap-1">
                      <div className="fs-20 fw-600 textFontColor">{sanitizeInput(viewStopDetails.customer_name ?? "")}</div>
                      <div className="d-flex align-items-center justify-content-start">
                        <img src={locationIcon} alt="location" />
                        <span className="ms-2 fs-13 fw-400 " style={{ color: "#A2A7AE" }}>
                          {sanitizeInput(viewStopDetails.location ?? "")}
                        </span>
                        <div
                          className="mx-2"
                          style={{
                            width: "5px",
                            height: "5px",
                            borderRadius: "100px",
                            background: "#D9D9D9",
                          }}
                        ></div>
                        <div className="d-flex align-items-center justify-content-center rounded-4 px-2 py-1" style={{ background: "#EDF2FF", color: "#3469E7" }}>
                          <span className="fs-12 fw-500">#{sanitizeInput(viewStopDetails.customer_code ?? "")}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="d-flex align-items-center justify-content-start me-2">
                <Dropdown>
                  <Dropdown.Toggle
                    bsPrefix="custom-toggle"
                    variant="light"
                    id="dropdown-sort"
                    className="d-flex align-items-center justify-content-between backgroundLightOrange rounded-5 status-badge-toggle"
                  >
                    <span className="d-flex align-items-center px-1 gap-2">
                      <span className="backgroundYellow status-dot-view"></span>

                      <span className="dropdown-text textcolor fs-13 fw-500">{sanitizeInput(viewStopDetails.status ?? "-")}</span>
                    </span>
                  </Dropdown.Toggle>
                </Dropdown>

                <div className="ms-2">
                  {(checkPermission("edit_permission") || checkPermission("delete_permission")) && viewStopDetails.status === "Created" && (
                    <Dropdown>
                      <Dropdown.Toggle
                        bsPrefix="custom-toggle"
                        variant="light"
                        id="dropdown-sort"
                        className="d-flex align-items-center justify-content-between rounded-5 more-options-toggle bg-white"
                      >
                        <span className="d-flex align-items-center px-1 gap-2">
                          <img src={moreIcon} alt="more" />
                        </span>
                      </Dropdown.Toggle>

                      <Dropdown.Menu className="more-options-menu">
                        {checkPermission("edit_permission") && viewStopDetails.status === "Created" && (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            onClick={() => {
                              navigate("/stops/create", {
                                state: { id: viewStopDetails.id },
                              });
                            }}
                          >
                            Edit
                          </Dropdown.Item>
                        )}
                        {checkPermission("delete_permission") && viewStopDetails.status === "Created" && (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            onClick={() => {
                              setDeleteId(viewStopDetails.id);
                            }}
                          >
                            Delete
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>
              </div>
            </div>
            <div className="w-100 mb-1 d-flex align-items-center justify-content-start tab-bar-container">
              <div
                className={`tab-item d-flex align-items-center justify-content-center px-2 fs-13 ${selectedTab === "stops" ? "fw-500 active-bottom-bar " : " fw-400"} `}
                style={{
                  color: selectedTab === "stops" ? "#3469E7" : "#5F5E5B",
                }}
                onClick={() => {
                  setSelectedTab("stops");
                }}
              >
                <img src={selectedTab === "stops" ? activeStop : locationIcon} alt="tab" className="me-1" /> Stop details
              </div>
              <div
                className={`tab-item d-flex align-items-center justify-content-center px-2 fs-13 ${selectedTab === "workflow" ? "fw-500 active-bottom-bar " : " fw-400"} `}
                style={{
                  color: selectedTab === "workflow" ? "#3469E7" : "#5F5E5B",
                }}
                onClick={() => {
                  setSelectedTab("workflow");
                }}
              >
                <img src={selectedTab === "workflow" ? activeWorkflow : workflowIcon} alt="tab" className="me-1" /> Workflow
              </div>
            </div>
            {selectedTab === "stops" && (
              <>
                <div className="row info-cards-row">
                  <div className="col-12 col-lg-5 mt-2">
                    <div className="info-card d-flex flex-column align-items-start justify-content-start">
                      <div className="fs-16 fw-600 textFontColor">Stop information</div>

                      {isLoading ? (
                        <div className="w-100">
                          {Array.from({ length: 3 }).map((_, idx) => (
                            <div key={idx} className="mt-3 d-flex align-items-center justify-content-between">
                              <div className="skeleton skeleton-text" style={{ width: "150px", height: "13px" }} />
                              <div className="skeleton skeleton-text" style={{ width: "120px", height: "13px" }} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div className="info-label-width d-flex align-items-center justify-content-start">
                              <img src={locationIcon} alt="location" />
                              <span className="ms-2 fs-13 fw-500 cardTextGrey">Stop ID</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.stop_code ?? "")}</div>
                          </div>

                          <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div className="info-label-width d-flex align-items-center justify-content-start">
                              <img src={dateIc} alt="date" />
                              <span className="ms-2 fs-13 fw-500 cardTextGrey">Actual delivery date</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.act_delivery_date ?? "")}</div>
                          </div>

                          <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div className="info-label-width d-flex align-items-center justify-content-start">
                              <img src={dateIc} alt="date" />
                              <span className="ms-2 fs-13 fw-500 cardTextGrey">Estimated Delivery Date</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.est_delivery_date ?? "")}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-7 mt-2">
                    <div className="address-info-card d-flex flex-column align-items-start justify-content-start">
                      <div className="fs-16 fw-600 textFontColor">Address information</div>

                      {isLoading ? (
                        <>
                          <div className="mt-2 w-100">
                            <div className="skeleton" style={{ width: "100%", height: "8px", borderRadius: "4px" }} />
                          </div>
                          <div className="w-100 mt-3 d-flex align-items-center justify-content-between">
                            <div className="d-flex flex-column align-items-start gap-2">
                              <div className="skeleton skeleton-text" style={{ width: "60px", height: "11px" }} />
                              <div className="skeleton skeleton-text" style={{ width: "200px", height: "13px" }} />
                            </div>
                            <div className="d-flex flex-column align-items-end gap-2">
                              <div className="skeleton skeleton-text" style={{ width: "60px", height: "11px" }} />
                              <div className="skeleton skeleton-text" style={{ width: "200px", height: "13px" }} />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mt-2 w-100">
                            <TruckProgressBar progress={50} />
                          </div>
                          <div className="w-100 mt-2 me-2 d-flex align-items-center justify-content-between">
                            <div className="d-flex flex-column align-items-start justify-content-start">
                              <div className="fs-11 fw-700 cardTextGrey">FROM </div>
                              <div className="mt-1 fs-13 fw-7300 textFontColor d-flex flex-wrap justify-content-start">{sanitizeInput(viewStopDetails.fromaddress ?? "")} </div>
                            </div>

                            <div className="ms-2 d-flex flex-column align-items-end justify-content-start">
                              <div className="fs-11 fw-700 cardTextGrey">TO </div>
                              <div className="mt-1 fs-13 fw-7300 textFontColor d-flex flex-wrap justify-content-start">{sanitizeInput(viewStopDetails.toaddress ?? "")} </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="my-2 info-card">
                  <div className="row">
                    <div className="col-12 ">
                      <div className="row">
                        <div className="col-xl-12 d-flex justify-content-between">
                          <p className="fs-18 fw-600 mb-0">Product </p>
                          {isLoading ? (
                            <div className="skeleton" style={{ width: "150px", height: "36px", borderRadius: "8px" }} />
                          ) : (
                            <div className="total-amount-badge d-flex justify-content-start">
                              <img src={dollerIc} alt="doller" />
                              <span className="ms-2 fs-13 fw-400 cardTextGrey">Total :</span> <span className="ms-2 fs-14 fw-500 textFontColor">₹ {sanitizeInput(viewStopDetails.total_amount ?? "0")}</span>
                            </div>
                          )}
                        </div>
                        <div style={{ overflowY: "visible" }}>
                          <div className="mt-2 product-table-wrapper ">
                            <table className="product-table" border="1" cellPadding="10">
                              <thead>
                                <tr>
                                  <th className="ps-2">PRODUCT NAME</th>
                                  <th className="ps-2">WEIGHT</th>
                                  <th className="ps-2">VOLUME</th>
                                  <th className="ps-2">UNIT</th>
                                  <th className="ps-2">QUANTITY</th>
                                  <th className="ps-2">PRICE</th>
                                </tr>
                              </thead>
                              <tbody>
                                {isLoading
                                  ? Array.from({ length: 3 }).map((_, idx) => (
                                    <tr key={idx} style={{ height: "36px" }}>
                                      {Array.from({ length: 6 }).map((_, colIdx) => (
                                        <td key={colIdx} className="px-2 py-1">
                                          <div className="skeleton skeleton-text" style={{ width: "80%", height: "13px" }} />
                                        </td>
                                      ))}
                                    </tr>
                                  ))
                                  : viewStopDetails?.product?.map((row, index) => (
                                    <tr key={row.id}>
                                      <td className="px-2 py-1">
                                        <div className="pe-3 py-1">{sanitizeInput(row.item ?? "")}</div>
                                      </td>
                                      <td className="px-2 py-1">{sanitizeInput(row.weight ?? "")}</td>

                                      <td className="px-2 py-1">{sanitizeInput(row.voulme ?? "")}</td>

                                      <td className="px-2 py-1" style={{ width: "150px" }}>
                                        {sanitizeInput(row.unit ?? "")}
                                      </td>
                                      <td className="px-2 py-1">{sanitizeInput(row.quantity ?? "")}</td>
                                      <td className="px-2 py-1">{sanitizeInput(row.price ?? "")}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="my-2 info-card">
                  <div className="row">
                    <div className="col-12 ">
                      <div className="row">
                        <div className="col-xl-12 d-flex justify-content-start">
                          <p className="fs-18 fw-600 mb-0">Route for this stop </p>
                        </div>
                      </div>

                      <div className="w-100 mt-2">
                        {isLoading ? (
                          <div className="skeleton" style={{ width: "100%", height: "355px", borderRadius: "8px" }} />
                        ) : (
                          <MapContainer center={[30, 0]} zoom={2} className="map-container-view">
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                            {viewStopDetails.from_latitude !== null && viewStopDetails.from_latitude !== undefined && viewStopDetails.from_longitude !== null && viewStopDetails.from_longitude !== undefined && viewStopDetails.from_latitude !== "" && viewStopDetails.from_longitude !== "" && (
                              <Marker key={`FromLocation`} position={[viewStopDetails.from_latitude, viewStopDetails.from_longitude]} icon={createCustomFromMarker()}>
                                {viewStopDetails.fromaddress !== "" && (
                                  <Popup>
                                    <div
                                      style={{
                                        width: 320,
                                        background: "white",
                                        padding: 12,
                                        borderRadius: 12,
                                        boxShadow: "0 6px 18px rgba(14,30,37,0.08)",
                                      }}
                                    >
                                      {viewStopDetails.fromaddress ?? ""}
                                    </div>
                                  </Popup>
                                )}
                              </Marker>
                            )}

                            {viewStopDetails.to_latitude !== null && viewStopDetails.to_latitude !== undefined && viewStopDetails.to_longitude !== null && viewStopDetails.to_longitude !== undefined && viewStopDetails.to_latitude !== "" && viewStopDetails.to_longitude !== "" && (
                              <Marker key={`ToLocation`} position={[viewStopDetails.to_latitude, viewStopDetails.to_longitude]} icon={createCustomToMarker()}>
                                {viewStopDetails.toaddress && (
                                  <Popup>
                                    <div
                                      style={{
                                        width: 320,
                                        background: "white",
                                        padding: 12,
                                        borderRadius: 12,
                                        boxShadow: "0 6px 18px rgba(14,30,37,0.08)",
                                      }}
                                    >
                                      {viewStopDetails.toaddress ?? ""}
                                    </div>
                                  </Popup>
                                )}
                              </Marker>
                            )}
                            {viewStopDetails.map_route &&
                              (() => {
                                const points = viewStopDetails.map_route.split("-").map((point) => {
                                  const [lat, lng] = point.split(",");
                                  return [parseFloat(lat), parseFloat(lng)];
                                });
                                if (points.length >= 2) {
                                  return <RoadRoute from={points[0]} to={points[1]} />;
                                }
                                return null;
                              })()}
                          </MapContainer>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {selectedTab === "workflow" && (
              <WorkflowTab
                isWorkflowLoading={isWorkflowLoading}
                estimateWorkflow={estimateWorkflow}
                actualWorkflow={actualWorkflow}
                setSelectedStop={setSelectedStop}
                getSkippedMilestoneDetails={() => { }} // Stops don't seem to have this API yet
                setAttachmentUrls={setAttachmentUrls}
                setShowAttachmentModal={setShowAttachmentModal}
              />
            )}
          </div>
        </div>
      </div>

      {/* Profile Image Modal */}
      <ProfileImageModal show={showProfilePopup} onClose={() => setShowProfilePopup(false)} imageUrl={viewStopDetails.profile || ""} />

      <Modal show={showAttachmentModal} onHide={() => setShowAttachmentModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Attachment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center align-items-center bg-light p-0">
          {attachmentUrls.length > 0 && (
            <Carousel interval={null} className="w-100" style={{ background: "#333" }}>
              {attachmentUrls.map((url, index) => (
                <Carousel.Item key={index} className="text-center">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
                    <img
                      src={url}
                      alt={`Attachment ${index + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          )}
        </Modal.Body>
      </Modal>

      <CommonSnackbar
        show={showSnackbar}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        isSuccess={snackbarStatus}
      />
    </>
  );
};
